import { useMemo } from 'react';
import { isConsentPending, isMasterContext, useAuth } from '@/features/auth';
import { ONBOARDING_DEBUG } from '../constants/debug';

/** Account-scoped storage key, matching the one DocumentListPage writes. */
export function onboardingStorageKey(
  key: string,
  userId: string | undefined,
  accountId: string | undefined
): string {
  return userId ? `${key}_${userId}_${accountId ?? 'none'}` : key;
}

/** True once this account dismissed or completed onboarding in this browser. */
function isFinishedLocally(userId: string | undefined, accountId: string | undefined): boolean {
  try {
    return (
      localStorage.getItem(onboardingStorageKey('onboarding_finished', userId, accountId)) ===
      'true'
    );
  } catch {
    // Private mode / disabled storage — the server flag decides on its own.
    return false;
  }
}

/**
 * True while the onboarding modal would actually open for the current user. Waits for the
 * policies unless `ignorePolicies`; the GDPR decision comes after onboarding.
 */
export function useOnboardingPending({ ignorePolicies = false } = {}): boolean {
  const { user, isAuthenticated } = useAuth();
  const userId = user?.id;
  const accountId = user?.account?.id;

  // Memoized so the storage read stays out of the render phase — it only changes
  // when the account does, and the modal writes the key on its way out.
  const finishedLocally = useMemo(() => isFinishedLocally(userId, accountId), [userId, accountId]);

  // A local "finished" wins over the server flag: skipping fires the mutation
  // fire-and-forget, so a failed call must not trap the user in onboarding.
  if (finishedLocally) return false;
  if (!isAuthenticated || isMasterContext(user)) return false;
  if (!ignorePolicies && isConsentPending(user)) return false;
  if (ONBOARDING_DEBUG) return true;

  return user?.finishedOnboarding === false;
}
